import { parse } from 'query-string';

import { getValueFromKey, Locale, Region } from '@eon-home/react-library';

import { StringOrNull } from '../types';
import { getAccessToken } from '.';
import { LANGUAGE_QUERY_PARAM, REGION } from '../constants';
import { CMS_LANGS } from '@tools/constants/cms-v2';

export const getBrowserLanguage = (): StringOrNull => {
    const nav = window.navigator;
    const browserLanguagePropertyKeys = [
        'language',
        'browserLanguage',
        'systemLanguage',
        'userLanguage',
    ];

    let language = null;

    // support for HTML 5.1 "navigator.languages"
    if (Array.isArray(nav.languages)) {
        for (const languages of nav.languages) {
            language = languages;

            if (language && language.length) {
                break;
            }
        }
    }

    if (!language) {
        for (const langProps of browserLanguagePropertyKeys) {
            language = (nav as unknown as Record<string, string>)[langProps];

            if (language && language.length) {
                return language;
            }
        }
    }

    return language;
};

export const getUserLanguage = <T>(): keyof T => {
    const language = getBrowserLanguage() || Locale.EN;
    const browserLocale: string = language.split('-').shift()!.toUpperCase();

    return browserLocale as keyof T;
};

export const getDefaultLocale = (): Locale => {
    const browserLocale = getUserLanguage<typeof Locale>();
    return Locale[browserLocale] ? Locale[browserLocale] : Locale.EN;
};

export const getDefaultRegion = (): Region => {
    const browserLocale = getUserLanguage<typeof Region>();
    return Region[browserLocale] ?? Region.DE;
};

export const getRegion = (): Region => {
    const regions = Object.keys(CMS_LANGS);

    if (!getAccessToken()) {
        const queryString = parse(location.search);

        const regionAsParam = getValueFromKey(
            queryString,
            LANGUAGE_QUERY_PARAM,
        ) as string;

        if (regionAsParam && regions.includes(regionAsParam.toUpperCase())) {
            const region = regionAsParam.toUpperCase();

            localStorage.setItem(REGION, region);

            return region as Region;
        }
    }

    const savedRegion = localStorage.getItem(REGION);

    if (savedRegion && regions.includes(savedRegion as any)) {
        return savedRegion as Region;
    }

    const defaultRegion = getDefaultRegion();

    localStorage.setItem(REGION, defaultRegion);

    return defaultRegion;
};

const handleRegion = (regionList: Region[]) => regionList.includes(getRegion());

export const isItalianUser = () => handleRegion([Region.IT]);

export const isGermanUser = () => handleRegion([Region.DE]);

export const isHungarianUser = () => handleRegion([Region.HU]);
